/* ══════════════════════════════════════════
   Kleiner HTTP-Client auf fetch — statt axios
   ──────────────────────────────────────────
   ApiHandler brauchte von axios nur get/post mit JSON, Cookies und
   Zeitlimit. Dafür waren 52 KB (unkomprimiert) im Startbündel, auf dem
   Handy rund 70 ms zusätzliche Ladezeit plus Parsen. Hier dieselbe
   Schnittstelle, soweit ApiHandler sie nutzt, damit dort kein Fehlerpfad
   anders laufen muss:

     create({ timeout })            → { get, post }
     get(url, { params, withCredentials, timeout })
     post(url, data, { withCredentials, timeout })
       → Promise<{ data, status }>        bei 2xx
       → Error mit .response {status,data} bei anderem Status
       → Error mit .code 'ECONNABORTED'   bei Zeitüberschreitung
       → Error mit .request (ohne .response) bei Netzfehlern

   Genau diese drei Formen wertet ApiHandler._post aus.
   ══════════════════════════════════════════ */

async function lies(antwort) {
  const text = await antwort.text();
  if (!text) return '';
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function anfrage(methode, url, daten, optionen, standardZeit) {
  const { params, withCredentials, timeout = standardZeit } = optionen || {};
  let ziel = url;
  if (params && typeof params === 'object') {
    const q = new URLSearchParams();
    for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null) q.append(k, v);
    const s = q.toString();
    if (s) ziel += (ziel.includes('?') ? '&' : '?') + s;
  }

  const abbruch = new AbortController();
  let abgelaufen = false;
  const uhr = timeout
    ? setTimeout(() => {
        abgelaufen = true;
        abbruch.abort();
      }, timeout)
    : null;

  let antwort;
  try {
    antwort = await fetch(ziel, {
      method: methode,
      credentials: withCredentials ? 'include' : 'same-origin',
      headers: daten !== undefined ? { 'Content-Type': 'application/json' } : undefined,
      body: daten !== undefined ? JSON.stringify(daten) : undefined,
      signal: abbruch.signal,
    });
  } catch (ursache) {
    clearTimeout(uhr);
    const fehler = new Error(abgelaufen ? `timeout of ${timeout}ms exceeded` : 'Network Error');
    fehler.code = abgelaufen ? 'ECONNABORTED' : 'ERR_NETWORK';
    fehler.request = {};
    fehler.cause = ursache;
    throw fehler;
  }

  try {
    const data = await lies(antwort);
    if (antwort.status < 200 || antwort.status >= 300) {
      const fehler = new Error(`Request failed with status code ${antwort.status}`);
      fehler.code = 'ERR_BAD_RESPONSE';
      fehler.request = {};
      fehler.response = { status: antwort.status, data };
      throw fehler;
    }
    return { data, status: antwort.status };
  } catch (fehler) {
    if (fehler.response) throw fehler;
    // Abbruch mitten im Lesen des Körpers
    const f = new Error(abgelaufen ? `timeout of ${timeout}ms exceeded` : 'Network Error');
    f.code = abgelaufen ? 'ECONNABORTED' : 'ERR_NETWORK';
    f.request = {};
    throw f;
  } finally {
    clearTimeout(uhr);
  }
}

export function create({ timeout = 0 } = {}) {
  return {
    get: (url, optionen) => anfrage('GET', url, undefined, optionen, timeout),
    post: (url, daten = {}, optionen) => anfrage('POST', url, daten, optionen, timeout),
  };
}

const http = { create };
export default http;
